// Filtros da listagem de templates (status/disponibilidade via URL).
import { SearchIcon, Trash2Icon } from "lucide-react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { ComboBox } from "@/components/ui/combo-box"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { useNotificationCenterTemplateSearchParams } from "@/hooks"

export const TemplateFilters = () => {
  const { t } = useTranslation()
  const { params, searchParams, hasParams } =
    useNotificationCenterTemplateSearchParams()
  const navigate = useNavigate()
  const form = useForm({
    defaultValues: {
      name: params.name,
      active: params.active,
      enabled: params.enabled,
      channel: params.channel,
    },
  })
  const statusOptions = [
    {
      label: t(
        "pages.notificationCenter.templatesPage.filters.activeOption"
      ),
      value: "true",
    },
    {
      label: t(
        "pages.notificationCenter.templatesPage.filters.inactiveOption"
      ),
      value: "false",
    },
  ]
  // Valores casam com o enum NotificationChannel do backend
  // (filtro repassado como querystring channel=).
  const channelOptions = [
    {
      label: t(
        "pages.notificationCenter.templatesPage.filters.channelEmailOption"
      ),
      value: "EMAIL",
    },
    {
      label: t(
        "pages.notificationCenter.templatesPage.filters.channelWhatsappOption"
      ),
      value: "WHATSAPP",
    },
    {
      label: t(
        "pages.notificationCenter.templatesPage.filters.channelSmsOption"
      ),
      value: "SMS",
    },
  ]
  const handleSubmit = form.handleSubmit((data) => {
    const nextParams = new URLSearchParams(searchParams)
    Object.entries(data).forEach(([key, value]) => {
      if (value) {
        nextParams.set(key, value)
      } else {
        nextParams.delete(key)
      }
    })
    navigate(`?${nextParams.toString()}`)
  })
  const handleClear = () => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("whiteLabelId")
    nextParams.delete("active")
    nextParams.delete("enabled")
    nextParams.delete("channel")
    nextParams.delete("name")
    navigate(`?${nextParams.toString()}`)
  }
  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="grid gap-4 @2xl:grid-cols-[1fr_auto]"
      >
        <div className="grid gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder={t(
                      "pages.notificationCenter.templatesPage.filters.namePlaceholder"
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="grid gap-4 @xl:grid-cols-3">
            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ComboBox
                      items={statusOptions}
                      onSelect={field.onChange}
                      selected={field.value}
                      placeholder={t(
                        "pages.notificationCenter.templatesPage.filters.status"
                      )}
                      placeholderCommand={t(
                        "pages.notificationCenter.templatesPage.filters.statusSearch"
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ComboBox
                      items={statusOptions}
                      onSelect={field.onChange}
                      selected={field.value}
                      placeholder={t(
                        "pages.notificationCenter.templatesPage.filters.availability"
                      )}
                      placeholderCommand={t(
                        "pages.notificationCenter.templatesPage.filters.availabilitySearch"
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="channel"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ComboBox
                      items={channelOptions}
                      onSelect={field.onChange}
                      selected={field.value}
                      placeholder={t(
                        "pages.notificationCenter.templatesPage.filters.channel"
                      )}
                      placeholderCommand={t(
                        "pages.notificationCenter.templatesPage.filters.channelSearch"
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button type="submit" className="btn-whitelabel">
            <SearchIcon
              className="mr-2 size-5"
              aria-hidden="true"
            />
            {t(
              "pages.notificationCenter.templatesPage.filters.search"
            )}
          </Button>
          {hasParams && (
            <Button
              variant="outline"
              type="button"
              onClick={handleClear}
            >
              <Trash2Icon
                className="mr-2 size-5"
                aria-hidden="true"
              />
              {t(
                "pages.notificationCenter.templatesPage.filters.clear"
              )}
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}
